<template>
  <div v-if="visible" class="modal-overlay" @click.self="$emit('cerrar')">
    <div class="modal-content">
      <div class="modal-header">
        <h3>Antecedentes</h3>
        <button class="btn-cerrar" @click="$emit('cerrar')">✕</button>
      </div>
      <div class="modal-body">
        <div class="campo">
          <div class="campo-header">
            <label for="ant-alergias">Alergias</label>
            <div class="campo-colores">
              <button type="button" class="color-clear" :class="{ activo: !form.alertaAlergias }" @click="form.alertaAlergias = ''">✕</button>
              <button v-for="c in COLORES_ALERTA" :key="c" type="button" class="color-dot" :class="{ seleccionado: form.alertaAlergias === c }" :style="{ background: c }" @click="form.alertaAlergias = c" />
            </div>
          </div>
          <!-- autocomplete="off": es dato de salud, no debe quedar en el autofill del navegador. -->
          <textarea v-model="form.alergias" id="ant-alergias" name="ant-alergias" autocomplete="off" rows="3" placeholder="Sin alergias registradas"></textarea>
        </div>

        <div class="campo">
          <div class="campo-header">
            <label for="ant-enfermedades">Enfermedades</label>
            <div class="campo-colores">
              <button type="button" class="color-clear" :class="{ activo: !form.alertaEnfermedades }" @click="form.alertaEnfermedades = ''">✕</button>
              <button v-for="c in COLORES_ALERTA" :key="c" type="button" class="color-dot" :class="{ seleccionado: form.alertaEnfermedades === c }" :style="{ background: c }" @click="form.alertaEnfermedades = c" />
            </div>
          </div>
          <textarea v-model="form.enfermedades" id="ant-enfermedades" name="ant-enfermedades" autocomplete="off" rows="3" placeholder="Sin enfermedades registradas"></textarea>
        </div>

        <div class="campo">
          <div class="campo-header">
            <label for="ant-observaciones">Observaciones</label>
            <div class="campo-colores">
              <button type="button" class="color-clear" :class="{ activo: !form.alertaObservaciones }" @click="form.alertaObservaciones = ''">✕</button>
              <button v-for="c in COLORES_ALERTA" :key="c" type="button" class="color-dot" :class="{ seleccionado: form.alertaObservaciones === c }" :style="{ background: c }" @click="form.alertaObservaciones = c" />
            </div>
          </div>
          <textarea v-model="form.observaciones" id="ant-observaciones" name="ant-observaciones" autocomplete="off" rows="3" placeholder="Sin observaciones"></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-cancelar" @click="$emit('cerrar')">Cancelar</button>
        <button class="btn-guardar" @click="guardar">Guardar</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, watch, onUnmounted } from 'vue';
import { COLORES_ALERTA } from '@/constants/alertasMedicas';
import { useScrollLock } from '@/composables/useScrollLock';
import { marcarEdicion } from '@/composables/edicionPendiente';

const props = defineProps({
  visible: { type: Boolean, default: false },
  alergias: { type: String, default: '' },
  enfermedades: { type: String, default: '' },
  observaciones: { type: String, default: '' },
  alertaAlergias: { type: String, default: '' },
  alertaEnfermedades: { type: String, default: '' },
  alertaObservaciones: { type: String, default: '' },
});

const emit = defineEmits(['cerrar', 'guardar']);

useScrollLock(() => props.visible);

const form = reactive({
  alergias: '',
  enfermedades: '',
  observaciones: '',
  alertaAlergias: '',
  alertaEnfermedades: '',
  alertaObservaciones: '',
});

watch(() => props.visible, (val) => {
  // Mientras el modal esté abierto, marcar edición pendiente (no auto-recargar).
  marcarEdicion('antecedentes', val);
  if (val) {
    form.alergias = props.alergias || '';
    form.enfermedades = props.enfermedades || '';
    form.observaciones = props.observaciones || '';
    form.alertaAlergias = props.alertaAlergias || '';
    form.alertaEnfermedades = props.alertaEnfermedades || '';
    form.alertaObservaciones = props.alertaObservaciones || '';
  }
});

function guardar() {
  emit('guardar', {
    alergias: form.alergias,
    enfermedades: form.enfermedades,
    observaciones: form.observaciones,
    alertaAlergias: form.alertaAlergias,
    alertaEnfermedades: form.alertaEnfermedades,
    alertaObservaciones: form.alertaObservaciones,
  });
  emit('cerrar');
}

onUnmounted(() => marcarEdicion('antecedentes', false));
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background: white;
  width: 90%;
  max-width: 480px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.2s ease-out;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

@keyframes slideIn {
  from { transform: translateY(-16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  padding: 16px 20px;
  background: #678AB2;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
}

.btn-cerrar {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
}

.campo {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.campo-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.campo-header label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.campo-colores {
  display: flex;
  align-items: center;
  gap: 5px;
}

.color-clear {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid #cbd5e1;
  background: white;
  color: #94a3b8;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: border-color 0.1s;
}

.color-clear.activo {
  border-color: #475569;
  color: #475569;
}

.color-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s, border-color 0.1s;
}

.color-dot:hover {
  transform: scale(1.15);
}

.color-dot.seleccionado {
  border-color: #1e293b;
  transform: scale(1.2);
}

.campo textarea {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-size: 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  width: 100%;
}

.campo textarea:focus {
  border-color: #678AB2;
}

.modal-footer {
  padding: 14px 20px;
  background: #f8f9fa;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-shrink: 0;
}

.btn-cancelar {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: #e5e7eb;
  color: #374151;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.btn-guardar {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: #678AB2;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.btn-guardar:hover {
  background: #4d6f96;
}
</style>
